import { AssetDoc } from '@livekatsomo/models';
import { nodeInputRule } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import { ReactNodeViewRenderer } from '@tiptap/react';
import NextImage from 'next/image';
import React, { Component, FC, HTMLAttributes, ReactElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { htmlToDOMOutputSpec } from './htmlToDOMOutputSpec';
import { ImageResizeNode } from './ImageResizeNode';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    imageResize: {
      setImage: (options: ImageResizeAttributes) => ReturnType;
    };
  }
}

export interface ImageOptions {
  inline: boolean;
  allowBase64: boolean;
  HTMLAttributes: Record<string, HTMLAttributes<'div'>>;
  resizeIcon: FC | Component | ReactElement;
  useFigure: boolean;
}

export interface ImageResizeAttributes {
  image: AssetDoc;
  width?: number;
  height?: number;
  isDraggable?: boolean;
  size?: string;
  justify?: 'flex-start' | 'center' | 'flex-end';
  float?: 'left' | 'right' | 'none' | 'inline-start' | 'inline-end';
}
export const inputRegex = /(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))$/;
export const ImageResize = Image.extend<ImageOptions>({
  name: 'imageResize',
  addOptions() {
    return {
      ...this.parent?.(),
      inline: false,
      allowBase64: false,
      HTMLAttributes: {},
      useFigure: false,
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      image: {
        default: null,
      },
      width: {
        default: 100,
        renderHTML: (attributes) => {
          return {
            width: attributes['width'],
          };
        },
      },
      'max-width': {
        default: '100%',
        renderHTML: (attributes) => {
          return {
            'max-width': attributes['max-width'],
          };
        },
      },
      height: {
        default: 100,
        renderHTML: (attributes) => {
          return {
            height: attributes['height'],
          };
        },
      },
      size: {
        default: 'small',
        renderHTML: (attributes) => {
          return {
            class: `size-${attributes['size']}`,
          };
        },
      },
      float: {
        default: 'none',
        renderHTML: (attributes) => {
          return {
            float: attributes['float'],
          };
        },
      },
      justify: {
        renderHTML: (attributes) => {
          return {
            'justify-content': attributes['justify-content'],
          };
        },
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: this.options.allowBase64
          ? 'img[src]'
          : 'img[src]:not([src^="data:"])',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes: { float } }) {
    const { justify } = node.attrs;
    const attrs = node.attrs as ImageResizeAttributes;
    // const dom = document.createElement('div');
    // dom.setAttribute('class', `image-wrapper image-float-${float}`);
    // dom.setAttribute('style', `display: inline-block; float: ${float}`);
    const element = React.createElement(NextImage, {
      src: attrs.image.downloadUrls?.jpeg1000x1000 || attrs.image.originalUrl,
      width: attrs.width || attrs.image.width || 100,
      height: attrs.height || attrs.image.height || 100,
      alt: attrs.image.alt || '',
      style: { maxWidth: '100%', height: 'auto' },
    });

    const contentDOM = htmlToDOMOutputSpec(renderToStaticMarkup(element));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const image: [string, ...any] = [
      'div',
      {
        style: `display: inline-block; float: ${float}`,
        class: `image-wrapper image-float-${float}`,
      },
      contentDOM,
    ];
    if (justify) {
      return [
        'div',
        {
          style: `display: flex; justify-content: ${justify};`,
          class: 'image-container',
        },
        contentDOM,
      ];
    }

    return image;
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageResizeNode, {
      className: 'image-resize-node-renderer',
    });
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: (match) => {
          const [, , alt, src, title, height, width, isDraggable, size, float] =
            match;
          return { src, alt, title, height, width, isDraggable, size, float };
        },
      }),
    ];
  },
});

export default ImageResize;
